import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Drawer,
  IconButton,
  Divider,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, MenuIcon, CloseIcon } from './Icons';
import { PRODUCT_URLS } from '../constants';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'Shop', path: '/shop' },
  { label: 'About', path: '/about' },
];

const Header: React.FC<{ cartItemsCount?: number }> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    if (path === '/products') {
      return location.pathname === '/products' || location.pathname === '/systems';
    }
    return location.pathname.startsWith(path);
  };

  const go = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backdropFilter: 'blur(18px)',
        backgroundColor: 'rgba(255,255,255,0.86)',
        borderBottom: '1px solid rgba(15,23,42,0.06)',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 64, md: 72 },
          }}
        >
          <Box
            onClick={() => go('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              cursor: 'pointer',
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src="/images/zendilogo.png"
              alt="Zendi"
              sx={{ height: 28, width: 'auto', display: 'block' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: '-0.04em',
                color: 'text.primary',
              }}
            >
              Zendi
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
          >
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => go(item.path)}
                sx={{
                  color: isActive(item.path) ? 'text.primary' : 'text.secondary',
                  fontWeight: isActive(item.path) ? 650 : 500,
                  px: 1.75,
                  minWidth: 0,
                  background: 'transparent',
                  '&:hover': {
                    background: 'transparent',
                    color: 'text.primary',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              component="a"
              href={PRODUCT_URLS.companion}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              endIcon={<ArrowUpRight size={15} />}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Companion
            </Button>
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'text.primary' }}
              aria-label="Open menu"
            >
              <MenuIcon size={22} />
            </IconButton>
          </Box>
        </Box>
      </Container>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 300, p: 2.5 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, letterSpacing: '-0.03em' }}>Zendi</Typography>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <CloseIcon size={20} />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={0.5}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              onClick={() => go(item.path)}
              sx={{
                justifyContent: 'flex-start',
                color: isActive(item.path) ? 'primary.dark' : 'text.primary',
                fontWeight: isActive(item.path) ? 700 : 500,
                bgcolor: isActive(item.path) ? 'rgba(14,165,233,0.08)' : 'transparent',
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
        <Button
          component="a"
          href={PRODUCT_URLS.companion}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          endIcon={<ArrowUpRight size={15} />}
        >
          Open Companion
        </Button>
        <Button
          component="a"
          href={PRODUCT_URLS.ipos}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          fullWidth
          sx={{ mt: 1.25 }}
          endIcon={<ArrowUpRight size={15} />}
        >
          Open iPOS
        </Button>
      </Drawer>
    </Box>
  );
};

export default Header;
